/**
 * Agent Management Dashboard - Admin interface for managing agents, indexes, and access
 * 
 * Features:
 * - View and edit the agent registry (profiles, descriptions, sample questions)
 * - View all indexes across environments
 * - Trigger sync/rebuild operations
 * - Browse SharePoint documents & track document changes
 * - View sync history, logs, and pipeline status
 * - Manage agent access control
 * 
 * Access: Super-admin only
 *
 * Tab components are split into individual files for maintainability:
 *   PipelineTab, KnowledgeBaseTab, ToolsTab, UsersTab, AgentRegistryTab
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FolderOpen, RefreshCw, ArrowLeft, Settings,
  AlertTriangle, Users, Bot, Database, Zap, Globe
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import './IndexingDashboard.css';

// Types
import type {
  IndexConfig, SyncHistoryItem, Environment,
  AgentToolsGroup, SharedTool, AgentPromptsData,
  AgentAccessData, RegistryData, GraphPlatformData
} from './types';

// Constants
import { API_BASE, authFetch } from './constants';

// Tab components
import { PipelineTab } from './PipelineTab';
import { KnowledgeBaseTab } from './KnowledgeBaseTab';
import { ToolsTab } from './ToolsTab';
import { UsersTab } from './UsersTab';
import { AgentRegistryTab } from './AgentRegistryTab';
import { GraphPlatformTab } from './GraphPlatformTab';
import { AgentEditorTab } from './AgentEditorTab';
import { KnowledgeSourceTab } from './KnowledgeSourceTab';

// ============================================================================
// Main Dashboard Component
// ============================================================================

interface IndexingDashboardProps {
  embedded?: boolean;
}

export const IndexingDashboard: React.FC<IndexingDashboardProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [indexes, setIndexes] = useState<IndexConfig[]>([]);
  const [syncHistory, setSyncHistory] = useState<SyncHistoryItem[]>([]);
  const [environments, setEnvironments] = useState<Record<string, Environment>>({});
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('lab');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pipeline' | 'knowledgebase' | 'tools' | 'users' | 'registry' | 'platform' | 'factory' | 'sources'>(() => {
    const saved = sessionStorage.getItem('askaldo_admin_active_tab');
    if (saved === 'pipeline' || saved === 'knowledgebase' || saved === 'tools' || saved === 'users' || saved === 'registry' || saved === 'platform' || saved === 'factory' || saved === 'sources') {
      return saved;
    }
    return 'knowledgebase';
  });
  const [syncingIndex, setSyncingIndex] = useState<string | null>(null);
  const [agentTools, setAgentTools] = useState<AgentToolsGroup[]>([]);
  const [sharedTools, setSharedTools] = useState<SharedTool[]>([]);
  const [agentPrompts, setAgentPrompts] = useState<Record<string, AgentPromptsData>>({});
  const [agentAccess, setAgentAccess] = useState<AgentAccessData | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [registryData, setRegistryData] = useState<RegistryData | null>(null);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [graphPlatformData, setGraphPlatformData] = useState<GraphPlatformData | null>(null);
  const [graphPlatformLoading, setGraphPlatformLoading] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('askaldo_admin_active_tab', activeTab);
  }, [activeTab]);



  // Fetch indexes
  const fetchIndexes = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/indexes?environment=${selectedEnvironment}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error(`Failed to fetch indexes: ${response.status}`);
      const data = await response.json();
      setIndexes(data.indexes || []);
    } catch (err) {
      console.error('Error fetching indexes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch indexes');
    }
  }, [selectedEnvironment]);

  // Fetch sync history
  const fetchSyncHistory = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/sync/history?limit=50`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error(`Failed to fetch sync history: ${response.status}`);
      const data = await response.json();
      setSyncHistory(data.history || []);
    } catch (err) {
      console.error('Error fetching sync history:', err);
    }
  }, []);

  // Fetch environments
  const fetchEnvironments = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/environments`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error(`Failed to fetch environments: ${response.status}`);
      const data = await response.json();
      setEnvironments(data.environments || {});
      if (data.currentEnvironment) {
        setSelectedEnvironment(data.currentEnvironment);
      }
    } catch (err) {
      console.error('Error fetching environments:', err);
      setEnvironments({
        dev: { name: 'dev', display_name: 'Development (DEV)', can_create: true, can_delete: true },
        lab: { name: 'lab', display_name: 'Lab (LAB)', can_create: true, can_delete: true },
        uat: { name: 'uat', display_name: 'Testing (UAT)', can_create: true, can_delete: false },
        prod: { name: 'prod', display_name: 'Production (PROD)', can_create: false, can_delete: false, requires_approval: false }
      });
    }
  }, []);

  // Fetch agent tools inventory
  const fetchTools = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/tools`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error(`Failed to fetch tools: ${response.status}`);
      const data = await response.json();
      setAgentTools(data.agents || []);
      setSharedTools(data.sharedTools || []);
      
      // Also fetch agent prompts
      try {
        const promptsResponse = await authFetch(`${API_BASE}/admin/indexing/agent-prompts`, {
          credentials: 'include'
        });
        if (promptsResponse.ok) {
          const promptsData = await promptsResponse.json();
          if (promptsData.success) {
            setAgentPrompts(promptsData.agents || {});
          }
        }
      } catch (promptErr) {
        console.warn('Could not fetch agent prompts:', promptErr);
      }
    } catch (err) {
      console.error('Error fetching tools:', err);
    }
  }, []);

  // Fetch agent access data
  const fetchAgentAccess = useCallback(async () => {
    setAccessLoading(true);
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/agent-access?environment=${selectedEnvironment}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(`Failed to fetch access data: ${response.status}`);
      const data = await response.json();
      setAgentAccess(data);
    } catch (err) {
      console.error('Error fetching agent access:', err);
    } finally {
      setAccessLoading(false);
    }
  }, [selectedEnvironment]);

  // Fetch agent registry
  const fetchRegistry = useCallback(async () => {
    setRegistryLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/admin/registry`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error(`Failed to fetch registry: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        setRegistryData(data.registry);
      }
    } catch (err) {
      console.error('Error fetching agent registry:', err);
    } finally {
      setRegistryLoading(false);
    }
  }, []);

  // Fetch graph platform data (all entities in parallel)
  const fetchGraphPlatform = useCallback(async () => {
    setGraphPlatformLoading(true);
    try {
      const [agentsRes, mcpRes, toolsRes, toolSetsRes, schemaRes, healthRes] = await Promise.all([
        authFetch(`${API_BASE}/admin/graph/agents`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
        authFetch(`${API_BASE}/admin/graph/mcp-servers`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
        authFetch(`${API_BASE}/admin/graph/tools`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
        authFetch(`${API_BASE}/admin/graph/toolsets`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
        authFetch(`${API_BASE}/admin/graph/schema`, { credentials: 'include' }).then(r => r.ok ? r.json() : { nodes: [], relationships: [] }),
        authFetch(`${API_BASE}/admin/graph/health`, { credentials: 'include' }).then(r => r.ok ? r.json() : { connected: false, error: 'Failed to reach graph API' }),
      ]);

      setGraphPlatformData({
        agents: Array.isArray(agentsRes) ? agentsRes : [],
        mcpServers: Array.isArray(mcpRes) ? mcpRes : [],
        tools: Array.isArray(toolsRes) ? toolsRes : [],
        toolSets: Array.isArray(toolSetsRes) ? toolSetsRes : [],
        schema: schemaRes,
        health: healthRes,
      });
    } catch (err) {
      console.error('Error fetching graph platform data:', err);
      setGraphPlatformData({
        agents: [], mcpServers: [], tools: [], toolSets: [],
        schema: { nodes: [], relationships: [] },
        health: { connected: false, error: err instanceof Error ? err.message : 'Unknown error' },
      });
    } finally {
      setGraphPlatformLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([
        fetchIndexes(),
        fetchSyncHistory(),
        fetchEnvironments(),
        fetchTools()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchIndexes, fetchSyncHistory, fetchEnvironments, fetchTools]);

  // Fetch registry on initial mount (default tab is knowledgebase which needs it)
  useEffect(() => {
    if (!registryData) fetchRegistry();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch access data when environment changes while on Access tab
  useEffect(() => {
    if (activeTab === 'users') {
      fetchAgentAccess();
    }
  }, [selectedEnvironment, activeTab, fetchAgentAccess]);

  // Trigger sync / reindex via function app
  const handleSync = async (indexId: string, mode: 'incremental' | 'full', imageMode?: string, maxVisionCalls?: number) => {
    if (syncingIndex) return;
    setSyncingIndex(indexId);
    try {
      const endpoint = mode === 'full' 
        ? `${API_BASE}/admin/indexing/indexes/${indexId}/rebuild`
        : `${API_BASE}/admin/indexing/indexes/${indexId}/sync`;
      
      // imageMode / maxVisionCalls are optional; omitted → indexer uses the
      // agent's configured default. See agent-image-citation-contract.md §6
      // and docs/architecture/IMAGE_MODE_REFERENCE.md (AA-219/AA-220).
      const body: Record<string, unknown> = { mode, environment: selectedEnvironment };
      if (imageMode) body.imageMode = imageMode;
      if (maxVisionCalls !== undefined) body.maxVisionCalls = maxVisionCalls;

      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || `Sync failed: ${response.status}`);
      }

      const result = data.result || {};
      const summary = [
        result.num_added ? `+${result.num_added} added` : null,
        result.num_updated ? `~${result.num_updated} updated` : null,
        result.num_deleted ? `-${result.num_deleted} deleted` : null,
        result.num_skipped ? `${result.num_skipped} unchanged` : null,
      ].filter(Boolean).join(', ') || 'No changes';

      const vision = data.vision;
      const visionSummary = vision
        ? `\n\nVision: ${vision.visionCallsMade} captioned, ${vision.visionCallsCached} cached` +
          `${vision.visionCallsCapped ? `, ${vision.visionCallsCapped} capped` : ''} (~$${Number(vision.estCostUsd || 0).toFixed(3)})`
        : '';

      alert(`✅ ${mode === 'full' ? 'Full rebuild' : 'Incremental sync'} completed for ${data.indexName || indexId} (${selectedEnvironment.toUpperCase()})\n\n${summary}\nVectors: ${data.vectorsBefore ?? '?'} → ${data.vectorsAfter ?? '?'}${visionSummary}`);
      await fetchSyncHistory();
      await fetchIndexes();
    } catch (err) {
      console.error('Error triggering sync:', err);
      alert(`❌ Failed to trigger sync: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSyncingIndex(null);
    }
  };

  // Toggle index enabled status
  const handleToggleEnabled = async (indexId: string, enabled: boolean) => {
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/indexes/${indexId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !enabled })
      });
      if (!response.ok) throw new Error(`Failed to update index: ${response.status}`);
      await fetchIndexes();
    } catch (err) {
      console.error('Error updating index:', err);
      alert(`Failed to update index: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // ---- Loading / Error states ----

  if (loading) {
    return (
      <div className={embedded ? 'idx-container idx-embedded' : 'idx-container'}>
        <div className="idx-loading">
          <div className="idx-spinner" />
          <p>Loading agent management...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? 'idx-container idx-embedded' : 'idx-container'}>
        <div className="idx-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  // ---- Render ----

  return (
    <div className={embedded ? 'idx-container idx-embedded' : 'idx-container'}>
      {/* Header - hidden when embedded in AgentDashboard */}
      {!embedded && (
        <header className="idx-header">
          <div className="idx-header-left">
            <button 
              className="idx-back-button"
              onClick={() => navigate(-1)}
              title="Back to Dashboard"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1>
                <Settings size={28} />
                Agent Management
              </h1>
              <p className="idx-subtitle">Manage agents, knowledge bases, tools, and access control</p>
            </div>
          </div>
          
        </header>
      )}

      {/* Tabs */}
      <nav className="idx-tabs">
        <button 
          className={`idx-tab ${activeTab === 'knowledgebase' ? 'active' : ''}`}
          onClick={() => { setActiveTab('knowledgebase'); if (!registryData) fetchRegistry(); }}
        >
          <FolderOpen size={18} />
          Knowledge Base
        </button>
        <button 
          className={`idx-tab ${activeTab === 'pipeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('pipeline')}
        >
          <RefreshCw size={18} />
          Pipeline
        </button>
        <button 
          className={`idx-tab ${activeTab === 'tools' ? 'active' : ''}`}
          onClick={() => setActiveTab('tools')}
        >
          <Settings size={18} />
          Tools ({agentTools.reduce((sum, a) => sum + a.toolCount, 0)})
        </button>
        <button 
          className={`idx-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={18} />
          Access
        </button>
        <button 
          className={`idx-tab ${activeTab === 'registry' ? 'active' : ''}`}
          onClick={() => { setActiveTab('registry'); if (!registryData) fetchRegistry(); }}
        >
          <Bot size={18} />
          Agents
        </button>
        <button 
          className={`idx-tab ${activeTab === 'platform' ? 'active' : ''}`}
          onClick={() => { setActiveTab('platform'); if (!graphPlatformData) fetchGraphPlatform(); }}
        >
          <Database size={18} />
          Platform
        </button>
        <button
          className={`idx-tab ${activeTab === 'factory' ? 'active' : ''}`}
          onClick={() => { setActiveTab('factory'); if (!graphPlatformData) fetchGraphPlatform(); }}
        >
          <Zap size={18} />
          Factory
        </button>
        <button
          className={`idx-tab ${activeTab === 'sources' ? 'active' : ''}`}
          onClick={() => setActiveTab('sources')}
        >
          <Globe size={18} />
          Sources
        </button>
      </nav>

      {/* Tab Content */}
      <main className="idx-content">
        {activeTab === 'pipeline' && (
          <PipelineTab
            history={syncHistory}
            selectedEnvironment={selectedEnvironment}
            onRefreshHistory={fetchSyncHistory}
          />
        )}
        
        {activeTab === 'knowledgebase' && (
          <KnowledgeBaseTab
            indexes={indexes}
            selectedEnvironment={selectedEnvironment}
            registryAgents={registryData?.agents || []}
            syncingIndex={syncingIndex}
            onSync={handleSync}
            onRefreshIndexes={fetchIndexes}
          />
        )}
        
        {activeTab === 'tools' && (
          <ToolsTab
            agentTools={agentTools}
            sharedTools={sharedTools}
            indexes={indexes}
            agentPrompts={agentPrompts}
            onRefresh={fetchTools}
          />
        )}
        
        {activeTab === 'users' && (
          <UsersTab
            agentAccess={agentAccess}
            loading={accessLoading}
            onRefresh={fetchAgentAccess}
            selectedEnvironment={selectedEnvironment}
          />
        )}
        
        {activeTab === 'registry' && (
          <AgentRegistryTab
            registryData={registryData}
            loading={registryLoading}
            onRefresh={fetchRegistry}
          />
        )}

        {activeTab === 'platform' && (
          <GraphPlatformTab
            data={graphPlatformData}
            loading={graphPlatformLoading}
            onRefresh={fetchGraphPlatform}
          />
        )}

        {activeTab === 'factory' && (
          <AgentEditorTab
            mcpServers={graphPlatformData?.mcpServers || []}
            toolSets={graphPlatformData?.toolSets}
            onRefresh={() => { fetchGraphPlatform(); }}
          />
        )}

        {activeTab === 'sources' && (
          <KnowledgeSourceTab
            onRefresh={() => { fetchGraphPlatform(); }}
          />
        )}
      </main>
    </div>
  );
};

export default IndexingDashboard;
